import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  COLUMN_FILTER_MIN_CHARS,
  shouldApplyTextColumnFilter,
  uniqueColumnSuggestions,
} from '../../utils/columnFilter';
import FormattedDateInput from '../ui/FormattedDateInput';
import DateRangePicker from '../ui/DateRangePicker';

const FILTER_DROP_VIEWPORT_PAD = 8;
const FILTER_DROP_WIDTH = 240;
const FILTER_DROP_WIDE_WIDTH = 248;
const FILTER_DROP_GAP = 4;

function estimateFilterDropHeight(filterType?: 'text' | 'date' | 'date-range'): number {
  if (filterType === 'date-range') return 320;
  if (filterType === 'date') return 72;
  return 56;
}

function clampFilterDropPos(
  btn: HTMLElement,
  dropWidth: number,
  dropHeight = 0,
): { top: number; left: number } {
  const rect = btn.getBoundingClientRect();
  const pad = FILTER_DROP_VIEWPORT_PAD;
  let top = rect.bottom + FILTER_DROP_GAP;

  if (dropHeight > 0) {
    const spaceBelow = window.innerHeight - rect.bottom - pad;
    const spaceAbove = rect.top - pad;
    const placeAbove = spaceBelow < dropHeight && spaceAbove >= dropHeight;
    if (placeAbove) {
      top = rect.top - dropHeight - FILTER_DROP_GAP;
    } else if (spaceBelow < dropHeight && spaceAbove > spaceBelow) {
      top = Math.max(pad, rect.top - dropHeight - FILTER_DROP_GAP);
    }
    top = Math.min(top, window.innerHeight - dropHeight - pad);
    top = Math.max(pad, top);
  }

  let left = rect.left;
  if (left + dropWidth > window.innerWidth - pad) {
    left = Math.max(pad, window.innerWidth - dropWidth - pad);
  }
  if (left < pad) left = pad;
  return { top, left };
}

export type Column<T> = {
  key: string;
  header: string;
  getValue: (row: T) => string;
  render?: (row: T) => React.ReactNode;
  /** Custom header label content; defaults to `header` text when omitted. */
  renderHeader?: () => React.ReactNode;
  sortable?: boolean;
  searchable?: boolean;
  /** Column search input type (data dictionary: date picker for date/time columns). */
  filterType?: 'text' | 'date' | 'date-range';
  /** ISO `YYYY-MM-DD` bounds for date / date-range column filters. */
  filterMinDate?: string;
  filterMaxDate?: string;
  /** Override default alphanumeric filter pattern for this column. */
  filterPattern?: RegExp;
  headerClassName?: string;
  cellClassName?: string;
};

type SortDir = 'asc' | 'desc';

export function DataTable<T>({
  title,
  legend,
  toolbarRight,
  loading = false,
  enableHeaderSearch = true,
  columns,
  rows,
  rowKey,
  rowClassName,
  rowsPerPageOptions = [5, 10, 15],
  defaultRowsPerPage = 5,
  emptyMessage,
  footerCountNoun,
  previousLabel,
  nextLabel,
  serverPaging,
  onFiltersChange,
  serverSort,
  fetchColumnSuggestions,
  emphasizedHeader = false,
  enableSorting = true,
  /** Inside `.cr-table-wrap` tab shell — no inner card border/radius (extranet Case Requests pattern). */
  embedded = false,
}: {
  title: string;
  /** Table legend (e.g. notification highlight); wrapped in `.legend` like extranet Case Requests. */
  legend?: React.ReactNode;
  toolbarRight?: React.ReactNode;
  loading?: boolean;
  embedded?: boolean;
  enableHeaderSearch?: boolean;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  rowClassName?: (row: T) => string;
  rowsPerPageOptions?: number[];
  defaultRowsPerPage?: number;
  /** Shown when the current page has zero rows (non-loading). */
  emptyMessage?: string;
  /** Noun in footer: "Showing a to b of c **entries**" */
  footerCountNoun?: string;
  previousLabel?: string;
  nextLabel?: string;
  /** Stronger column header row background (admin list tables). */
  emphasizedHeader?: boolean;
  /** When false, column headers are not sortable. */
  enableSorting?: boolean;
  /** When set, rows are treated as one server page (no client slicing). */
  serverPaging?: {
    totalCount: number;
    totalPages: number;
    page: number;
    pageSize: number;
    onPageChange: (nextPage: number) => void;
    onPageSizeChange: (nextSize: number) => void;
  };
  /** When set with server paging, column search triggers server refetch (parent debounces). */
  onFiltersChange?: (filters: Record<string, string>) => void;
  serverSort?: {
    key: string;
    dir: SortDir;
    onSortChange: (key: string, dir: SortDir) => void;
  };
  /** Fetches typeahead suggestions after {@link COLUMN_FILTER_MIN_CHARS} characters (server-paged tables). */
  fetchColumnSuggestions?: (colKey: string, query: string, signal?: AbortSignal) => Promise<string[]>;
}) {
  const { t } = useTranslation();
  const resolvedEmptyMessage = emptyMessage ?? t('common.noResultsFound');
  const resolvedFooterNoun = footerCountNoun ?? t('common.entries');
  const resolvedPreviousLabel = previousLabel ?? t('common.previous');
  const resolvedNextLabel = nextLabel ?? t('common.next');
  const resolvedFirstLabel = t('common.first');
  const resolvedLastLabel = t('common.last');

  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [rpp, setRpp] = useState(defaultRowsPerPage);
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);
  const [filterPos, setFilterPos] = useState<{ top: number; left: number } | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestAbortRef = useRef<AbortController | null>(null);
  const filterDropRef = useRef<HTMLDivElement | null>(null);
  const filterBtnRef = useRef<HTMLButtonElement | null>(null);

  const closeFilter = () => {
    setOpenDropdownKey(null);
    setFilterPos(null);
    filterBtnRef.current = null;
  };

  const updateFilterPos = () => {
    const btn = filterBtnRef.current;
    const drop = filterDropRef.current;
    if (!btn) return;
    const col = openDropdownKey ? columns.find((c) => c.key === openDropdownKey) : null;
    const fallbackWidth =
      col?.filterType === 'date-range' ? FILTER_DROP_WIDE_WIDTH : FILTER_DROP_WIDTH;
    const width = drop?.offsetWidth ?? fallbackWidth;
    const height = drop?.offsetHeight ?? estimateFilterDropHeight(col?.filterType);
    setFilterPos(clampFilterDropPos(btn, width, height));
  };

  const openFilterFor = (key: string, btn: HTMLButtonElement) => {
    if (openDropdownKey === key) {
      closeFilter();
      return;
    }
    filterBtnRef.current = btn;
    const col = columns.find((c) => c.key === key);
    const width = col?.filterType === 'date-range' ? FILTER_DROP_WIDE_WIDTH : FILTER_DROP_WIDTH;
    const height = estimateFilterDropHeight(col?.filterType);
    setFilterPos(clampFilterDropPos(btn, width, height));
    setOpenDropdownKey(key);
  };

  useEffect(() => {
    if (!openDropdownKey) return;
    const handler = (e: MouseEvent) => {
      const node = e.target as Node | null;
      const target = node instanceof Element ? node : node?.parentElement;
      if (!target) return;
      // Date pickers render their popovers via portal (`.cal-popover`), so treat them as "inside".
      if (target.closest('.cal-popover')) return;

      if (!target.closest('.col-srch-wrap') && !filterDropRef.current?.contains(target)) {
        closeFilter();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openDropdownKey]);

  useEffect(() => {
    if (!openDropdownKey) return;
    window.addEventListener('scroll', updateFilterPos, true);
    window.addEventListener('resize', updateFilterPos);
    return () => {
      window.removeEventListener('scroll', updateFilterPos, true);
      window.removeEventListener('resize', updateFilterPos);
    };
  }, [openDropdownKey]);

  useLayoutEffect(() => {
    if (!openDropdownKey || !filterBtnRef.current || !filterDropRef.current) return;
    const col = columns.find((c) => c.key === openDropdownKey);
    setFilterPos(
      clampFilterDropPos(
        filterBtnRef.current,
        filterDropRef.current.offsetWidth,
        filterDropRef.current.offsetHeight || estimateFilterDropHeight(col?.filterType),
      ),
    );
  }, [openDropdownKey, columns, filters]);

  const serverFilterMode = Boolean(serverPaging && onFiltersChange);

  const buildAppliedFilters = (source: Record<string, string>): Record<string, string> => {
    const payload: Record<string, string> = {};
    for (const [k, v] of Object.entries(source)) {
      const col = columns.find((c) => c.key === k);
      if (col?.filterType === 'date' || col?.filterType === 'date-range') {
        if (v.trim()) payload[k] = v;
        continue;
      }
      if (shouldApplyTextColumnFilter(v)) payload[k] = v;
    }
    return payload;
  };

  const commitFilters = (next: Record<string, string>, immediate = false) => {
    setPage(1);
    if (serverFilterMode) {
      const payload = buildAppliedFilters(next);
      if (immediate) {
        if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
        onFiltersChange?.(payload);
        return;
      }
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(() => onFiltersChange?.(payload), 400);
      return;
    }
  };

  const applyColumnFilter = (key: string, value: string, immediate = false) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    const col = columns.find((c) => c.key === key);
    commitFilters(next, immediate || col?.filterType === 'date' || col?.filterType === 'date-range');
  };

  const clearColumnFilter = (key: string) => {
    const next = { ...filters };
    delete next[key];
    setFilters(next);
    setSuggestions([]);
    commitFilters(next, true);
  };

  const selectSuggestion = (key: string, value: string) => {
    applyColumnFilter(key, value, true);
    closeFilter();
    setSuggestions([]);
  };

  useEffect(() => {
    if (!openDropdownKey) {
      setSuggestions([]);
      return;
    }
    const col = columns.find((c) => c.key === openDropdownKey);
    if (!col || col.filterType === 'date') {
      setSuggestions([]);
      return;
    }

    const query = filters[openDropdownKey]?.trim() ?? '';
    if (query.length < COLUMN_FILTER_MIN_CHARS) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    suggestAbortRef.current?.abort();
    suggestAbortRef.current = new AbortController();
    const { signal } = suggestAbortRef.current;
    setSuggestionsLoading(true);

    const load = fetchColumnSuggestions
      ? fetchColumnSuggestions(openDropdownKey, query, signal)
      : Promise.resolve(
          uniqueColumnSuggestions(
            rows.map((r) => col.getValue(r)),
            query,
          ),
        );

    void load
      .then((items) => {
        if (!signal.aborted) setSuggestions(items);
      })
      .finally(() => {
        if (!signal.aborted) setSuggestionsLoading(false);
      });

    return () => suggestAbortRef.current?.abort();
  }, [openDropdownKey, filters, fetchColumnSuggestions, columns, rows]);

  const filtered = useMemo(() => {
    if (serverFilterMode) return rows;
    const active = Object.entries(buildAppliedFilters(filters));
    if (active.length === 0) return rows;
    return rows.filter((r) =>
      active.every(([k, v]) => {
        const col = columns.find((c) => c.key === k);
        if (!col) return true;
        return col.getValue(r).toLowerCase().includes(v.toLowerCase());
      }),
    );
  }, [rows, filters, columns, serverFilterMode]);

  const sorted = useMemo(() => {
    if (serverPaging) return filtered;
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => dir * col.getValue(a).localeCompare(col.getValue(b)));
  }, [filtered, sort, columns, serverPaging]);

  const effectiveRpp = serverPaging ? serverPaging.pageSize : rpp;
  const total = serverPaging ? serverPaging.totalCount : sorted.length;
  const totalPages = serverPaging
    ? Math.max(1, serverPaging.totalPages)
    : Math.max(1, Math.ceil((total || 0) / effectiveRpp));
  const safePage = serverPaging
    ? Math.min(Math.max(1, serverPaging.page), totalPages)
    : Math.min(page, totalPages);
  const start = (safePage - 1) * effectiveRpp;
  const pageRows = serverPaging
    ? filtered.length > effectiveRpp
      ? filtered.slice(start, start + effectiveRpp)
      : filtered
    : sorted.slice(start, start + effectiveRpp);

  const from = total === 0 ? 0 : start + 1;
  const to = total === 0 ? 0 : start + pageRows.length;

  const pagerPages = useMemo(() => {
    if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (safePage === 1) return [1, 2, 3];
    if (safePage >= totalPages) return [totalPages - 2, totalPages - 1, totalPages];
    return [safePage - 1, safePage, safePage + 1];
  }, [totalPages, safePage]);

  const wrapClass = [
    embedded ? 'dt-embedded' : 'cr-table-wrap',
    emphasizedHeader ? 'dt-head-em' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const openFilterCol = openDropdownKey ? columns.find((c) => c.key === openDropdownKey) : null;

  return (
    <div className={wrapClass}>
      <div className="tbl-toolbar">
        <div className="tbl-toolbar-title">{title}</div>
        {legend ? (
          <div className="legend">{legend}</div>
        ) : toolbarRight ? (
          toolbarRight
        ) : (
          <div />
        )}
      </div>
      <div className="tw">
        <table className="dt">
          <thead>
            <tr>
              {columns.map((c) => {
                const isSortable =
                  enableSorting && c.sortable !== false && (!serverPaging || Boolean(serverSort));
                const isSearchable = c.searchable !== false;
                const activeSortKey = serverSort?.key ?? sort?.key;
                const activeSortDir = serverSort?.dir ?? sort?.dir;
                const sortClass =
                  activeSortKey === c.key ? `th-sort ${activeSortDir}` : 'th-sort';
                const isOpen = openDropdownKey === c.key;
                return (
                  <th key={c.key} className={c.headerClassName}>
                    <div
                      className="th-inner"
                      style={isSortable ? undefined : { cursor: 'default' }}
                      onClick={() => {
                        if (!isSortable) return;
                        if (serverSort) {
                          const nextDir =
                            serverSort.key === c.key && serverSort.dir === 'asc' ? 'desc' : 'asc';
                          serverSort.onSortChange(c.key, nextDir);
                          return;
                        }
                        if (serverPaging) return;
                        setSort((prev) => {
                          if (!prev || prev.key !== c.key) return { key: c.key, dir: 'asc' };
                          return { key: c.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
                        });
                        setPage(1);
                      }}
                    >
                      <span className={`th-label${c.renderHeader ? ' th-label--custom' : ''}`}>
                        {c.renderHeader ? c.renderHeader() : c.header}
                      </span>
                      {isSortable ? (
                        <div className={sortClass}>
                          <span className="arr-up" />
                          <span className="arr-dn" />
                        </div>
                      ) : null}
                      {enableHeaderSearch && isSearchable ? (
                        <div className="col-srch-wrap" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className={`col-srch-btn${isOpen ? ' active' : ''}${filters[c.key]?.trim() ? ' on' : ''}`}
                            aria-label={`Search ${c.header}`}
                            aria-expanded={isOpen}
                            onClick={(e) => openFilterFor(c.key, e.currentTarget)}
                          >
                            {c.filterType === 'date' || c.filterType === 'date-range' ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                              </svg>
                            )}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="dt-status-cell">
                  {t('common.loading')}
                </td>
              </tr>
            ) : (
              <>
                {pageRows.map((r) => (
                  <tr key={rowKey(r)} className={rowClassName ? rowClassName(r) : undefined}>
                    {columns.map((c) => (
                      <td key={c.key} className={c.cellClassName}>
                        {c.render ? c.render(r) : c.getValue(r)}
                      </td>
                    ))}
                  </tr>
                ))}
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="dt-empty-cell">
                      {resolvedEmptyMessage}
                    </td>
                  </tr>
                ) : null}
              </>
            )}
          </tbody>
        </table>
      </div>
      <div className="tft">
        <div className="tfi">
          {t('common.showingEntries', { from, to, total, noun: resolvedFooterNoun })}
        </div>
        <div className="pgr">
          <span className="rpl">{t('common.rowsPerPage')}</span>
          <select
            className="rpsel"
            value={effectiveRpp}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (serverPaging) {
                serverPaging.onPageSizeChange(n);
                serverPaging.onPageChange(1);
              } else {
                setRpp(n);
                setPage(1);
              }
            }}
          >
            {rowsPerPageOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <div className="pgg">
            {serverPaging && safePage > 1 ? (
              <button
                className="pgb"
                type="button"
                onClick={() => serverPaging.onPageChange(1)}
              >
                {resolvedFirstLabel}
              </button>
            ) : null}
            <button
              className={`pgb ${safePage <= 1 ? 'dis' : ''}`}
              type="button"
              onClick={() => {
                if (safePage <= 1) return;
                if (serverPaging) serverPaging.onPageChange(safePage - 1);
                else setPage(safePage - 1);
              }}
            >
              {resolvedPreviousLabel}
            </button>
            {pagerPages.map((p) => (
              <button
                key={p}
                className={`pgb ${p === safePage ? 'on' : ''}`}
                type="button"
                onClick={() => {
                  if (serverPaging) serverPaging.onPageChange(p);
                  else setPage(p);
                }}
              >
                {p}
              </button>
            ))}
            <button
              className={`pgb ${safePage >= totalPages ? 'dis' : ''}`}
              type="button"
              onClick={() => {
                if (safePage >= totalPages) return;
                if (serverPaging) serverPaging.onPageChange(safePage + 1);
                else setPage(safePage + 1);
              }}
            >
              {resolvedNextLabel}
            </button>
            {serverPaging && safePage < totalPages ? (
              <button
                className="pgb"
                type="button"
                onClick={() => serverPaging.onPageChange(totalPages)}
              >
                {resolvedLastLabel}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {openFilterCol && filterPos
        ? createPortal(
            <div
              ref={filterDropRef}
              className={`col-srch-drop open${openFilterCol.filterType === 'date-range' ? ' col-srch-drop--wide' : ''}`}
              style={{
                position: 'fixed',
                top: filterPos.top,
                left: filterPos.left,
                zIndex: 9999,
                ...(openFilterCol.filterType === 'date-range'
                  ? {
                      maxHeight: `calc(100vh - ${FILTER_DROP_VIEWPORT_PAD * 2}px)`,
                      overflowY: 'auto',
                    }
                  : {}),
              }}
            >
              {openFilterCol.filterType === 'date-range' ? (() => {
                const raw = filters[openFilterCol.key] ?? '';
                const pipe = raw.indexOf('|');
                const drFrom = pipe >= 0 ? raw.slice(0, pipe) : '';
                const drTo = pipe >= 0 ? raw.slice(pipe + 1) : '';
                return (
                  <div style={{ padding: 8 }}>
                    <DateRangePicker
                      from={drFrom}
                      to={drTo}
                      minDate={openFilterCol.filterMinDate}
                      maxDate={openFilterCol.filterMaxDate}
                      onChange={(f, t) =>
                        applyColumnFilter(openFilterCol.key, f || t ? `${f}|${t}` : '', true)
                      }
                    />
                  </div>
                );
              })() : openFilterCol.filterType === 'date' ? (
                <div style={{ padding: 8 }}>
                  <FormattedDateInput
                    className="ccr-inp"
                    value={filters[openFilterCol.key] ?? ''}
                    enableYmdViews
                    minDate={openFilterCol.filterMinDate}
                    maxDate={openFilterCol.filterMaxDate}
                    onChange={(value) => {
                      applyColumnFilter(openFilterCol.key, value, true);
                      closeFilter();
                    }}
                  />
                </div>
              ) : (
                <>
                  <div className="col-srch-input-wrap">
                    <input
                      type="text"
                      placeholder={t('common.typeAtLeastLetters', { count: COLUMN_FILTER_MIN_CHARS })}
                      maxLength={50}
                      value={filters[openFilterCol.key] ?? ''}
                      autoFocus
                      onChange={(e) => {
                        const value = e.target.value;
                        const pattern =
                          openFilterCol.filterPattern ?? /^[a-zA-Z0-9 /._-]*$/;
                        if (!pattern.test(value)) return;
                        applyColumnFilter(openFilterCol.key, value);
                      }}
                    />
                    {filters[openFilterCol.key]?.trim() ? (
                      <button
                        type="button"
                        className="col-srch-clear"
                        aria-label={`Clear ${openFilterCol.header} filter`}
                        onClick={() => clearColumnFilter(openFilterCol.key)}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                  {(filters[openFilterCol.key]?.trim().length ?? 0) >= COLUMN_FILTER_MIN_CHARS ? (
                    <ul
                      className="ms-chips-menu col-srch-suggest"
                      role="listbox"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {suggestionsLoading ? (
                        <li>
                          <span className="ms-chips-empty">{t('common.loading')}</span>
                        </li>
                      ) : suggestions.length === 0 ? (
                        <li>
                          <span className="ms-chips-empty">{t('common.noDataFound')}</span>
                        </li>
                      ) : (
                        suggestions.map((item) => (
                          <li key={item}>
                            <button
                              type="button"
                              role="option"
                              onClick={() => selectSuggestion(openFilterCol.key, item)}
                            >
                              {item}
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  ) : null}
                </>
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

